/**
 * 화상통화 녹화 서비스
 * - MediaRecorder API로 영상/음성 녹화
 * - Web Audio API로 로컬+리모트 오디오 믹싱
 */

class RecordingService {
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startTime: number = 0;
  private audioContext: AudioContext | null = null;
  private mixedStream: MediaStream | null = null;
  private compositeCanvas: HTMLCanvasElement | null = null;
  private animFrameId: number = 0;
  private audioDestination: MediaStreamAudioDestinationNode | null = null;
  // 이미 믹서에 넣은 스트림 — 녹화 중에 들어온 사람만 새로 더한다
  private mixedStreamIds = new Set<string>();

  get isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }

  getElapsedTime(): number {
    if (!this.startTime) return 0;
    return Math.floor((Date.now() - this.startTime) / 1000);
  }

  /**
   * 녹화 시작
   * @param localStream 로컬 미디어 스트림
   * @param remoteStreams 리모트 참가자 스트림 배열(녹화 중에 들어온 사람은 addAudioStream으로 더한다)
   * @param getRemoteVideos 지금 마운트된 리모트 비디오 엘리먼트 — 매 프레임 읽는다(녹화 중에 들어온 사람·바뀐 배치도 담긴다)
   * @param getLocalVideo 로컬 비디오 엘리먼트(카메라를 껐다 켜면 요소가 바뀐다)
   */
  startRecording(
    localStream: MediaStream,
    remoteStreams: MediaStream[],
    getRemoteVideos: () => HTMLVideoElement[],
    getLocalVideo: () => HTMLVideoElement | null,
  ): boolean {
    if (this.isRecording) return false;

    try {
      // 1) 오디오 믹싱: Web Audio API로 모든 오디오 트랙 합성
      this.audioContext = new AudioContext();
      const destination = this.audioContext.createMediaStreamDestination();
      this.audioDestination = destination;
      this.mixedStreamIds.clear();

      // 로컬 오디오 추가
      const localAudioTracks = localStream.getAudioTracks();
      if (localAudioTracks.length > 0) {
        const localAudioStream = new MediaStream(localAudioTracks);
        const localSource = this.audioContext.createMediaStreamSource(localAudioStream);
        localSource.connect(destination);
      }

      // 리모트 오디오 추가
      remoteStreams.forEach(stream => this.addAudioStream(stream));

      // 2) 비디오 합성: Canvas에 모든 비디오 프레임을 그림
      this.compositeCanvas = document.createElement('canvas');
      this.compositeCanvas.width = 1280;
      this.compositeCanvas.height = 720;
      const ctx = this.compositeCanvas.getContext('2d')!;

      const drawFrame = () => {
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, 1280, 720);

        const videos = getRemoteVideos().filter(v => v && !v.paused && v.videoWidth > 0);
        const localVideo = getLocalVideo();
        const totalVideos = videos.length;

        if (totalVideos === 0) {
          // 리모트 없으면 로컬만 크게 표시
          if (localVideo && !localVideo.paused && localVideo.videoWidth > 0) {
            ctx.drawImage(localVideo, 0, 0, 1280, 720);
          }
        } else if (totalVideos === 1) {
          // 리모트 1명: 전체 화면 + 로컬 PiP
          ctx.drawImage(videos[0], 0, 0, 1280, 720);
          if (localVideo && !localVideo.paused && localVideo.videoWidth > 0) {
            ctx.drawImage(localVideo, 1040, 540, 220, 160);
          }
        } else {
          // 다수: 그리드 레이아웃
          const cols = totalVideos <= 4 ? 2 : 3;
          const rows = Math.ceil(totalVideos / cols);
          const cellW = 1280 / cols;
          const cellH = 720 / rows;
          videos.forEach((video, i) => {
            const col = i % cols;
            const row = Math.floor(i / cols);
            ctx.drawImage(video, col * cellW, row * cellH, cellW, cellH);
          });
        }

        this.animFrameId = requestAnimationFrame(drawFrame);
      };
      drawFrame();

      // 3) Canvas 비디오 스트림 + 믹싱된 오디오를 합침
      const canvasStream = this.compositeCanvas.captureStream(30);
      this.mixedStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...destination.stream.getAudioTracks(),
      ]);

      // 4) MediaRecorder 시작
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
          ? 'video/webm;codecs=vp8,opus'
          : 'video/webm';

      this.chunks = [];
      this.mediaRecorder = new MediaRecorder(this.mixedStream, {
        mimeType,
        videoBitsPerSecond: 2_500_000,
      });

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          this.chunks.push(e.data);
        }
      };

      this.mediaRecorder.start(1000); // 1초마다 데이터 수집
      this.startTime = Date.now();
      return true;
    } catch (err) {
      console.error('[RecordingService] startRecording failed:', err);
      this.cleanup();
      return false;
    }
  }

  /** 녹화 중에 들어온 참가자의 소리를 더한다. 같은 스트림은 한 번만 */
  addAudioStream(stream: MediaStream) {
    if (!this.audioContext || !this.audioDestination) return;
    if (this.mixedStreamIds.has(stream.id)) return;
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) return;
    try {
      const source = this.audioContext.createMediaStreamSource(new MediaStream(audioTracks));
      source.connect(this.audioDestination);
      this.mixedStreamIds.add(stream.id);
    } catch (err) {
      console.warn('[RecordingService] 소리를 녹화에 더하지 못했습니다:', err);
    }
  }

  /**
   * 녹화 중지 → Blob 반환
   */
  stopRecording(): Promise<Blob | null> {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') {
        resolve(null);
        return;
      }

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: this.mediaRecorder?.mimeType || 'video/webm' });
        this.cleanup();
        resolve(blob);
      };

      this.mediaRecorder.stop();
    });
  }

  private cleanup() {
    cancelAnimationFrame(this.animFrameId);
    this.animFrameId = 0;

    this.audioContext?.close().catch(() => {});
    this.audioContext = null;
    this.audioDestination = null;
    this.mixedStreamIds.clear();

    this.mixedStream?.getTracks().forEach(t => t.stop());
    this.mixedStream = null;

    this.compositeCanvas = null;
    this.mediaRecorder = null;
    this.chunks = [];
    this.startTime = 0;
  }
}

export const recordingService = new RecordingService();
